import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "app/core/services/admin/auth/auth.service";
import { SwalModalService } from "app/core/services/global/modal/modal.service";
import { ToastService } from "app/core/services/global/toast/toast.service";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class ErrorInterceptor implements HttpInterceptor {
    constructor(private _authService: AuthService, private modal: SwalModalService, private translate: TranslateService, private _toaster: ToastService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        return next.handle(request).pipe(
            tap((response: HttpResponse<any>) => {
                // TODO: When we'll have the notification service we'll handle the response
            }),
            catchError((error: HttpErrorResponse) => {
                console.log("ERROR", error);
                if (request.headers.has("Content-Maestro") && request.headers.get("Content-Maestro") && "external-link" === request.headers.get("Content-Maestro")) {
                    // Used to not return an error when call specific external link like media-pim
                    return new Promise((resolve, reject) => {
                        resolve(true);
                    });
                } else {
                    return this._errorTreatment(error, request.method);
                }
            })
        );
    }

    private async _errorTreatment(httpError: HttpErrorResponse, httpVerb: string): Promise<any> {
        console.log(httpError, httpVerb);
        const url = httpError.url;

        if (this._authService.showError$.value) {
            let error;

            if (httpError.error instanceof Blob) {
                error = JSON.parse(await (httpError.error as any).text());
            } else {
                error = httpError.error;
            }

            // Network / CORS / server-down errors (status 0) and non-JSON bodies expose no
            // { key, method, type, data } envelope. Guard every access so the interceptor
            // degrades to a generic message instead of throwing an uncaught TypeError that
            // would break the current navigation.
            const status = httpError.status;
            const key = error?.key;
            const method = error?.method;
            const type = error?.type;

            if (401 === +status && !url.includes("/sign_in")) {
                return this._authService.logout(true); // Return to redirect
            } else {
                if (url && url.endsWith("graphql/graphql.json") && status === 0) {
                    console.log("Dashboard disabled");
                } else if ((key === "findGenrationData" && method === "getGenerationData") || (key === "generate" && method === "generate")) {
                    this._toaster.show({ type: "info", message: this.translate.instant(`response.error.${key}.${method}`) });
                } else if (url && url.endsWith("/generate.json") && "undefined" === key && "undefined" === method && type === "error") {
                    this._toaster.show({ type: "danger", message: this.translate.instant(`response.error.twig.render`) });
                } else {
                    let keyBase = "";
                    let keyFull = "";
                    let message = "";

                    if (url && url.endsWith("graphql/graphql.json")) {
                        message = error?.errors?.[0]?.message ?? "";
                    } else if (typeof error?.data === "string") {
                        message = error.data.split("Class :")[0].trim();
                    } else if (typeof error === "string") {
                        message = error.split("Class :")[0].trim();
                    } else {
                        // No parseable error body (network / CORS / status 0):
                        // fall back to the generic HTTP message built below.
                        message = "";
                    }

                    const responseParameters = (undefined !== (key && type && method) && "undefined" !== (key && type && method) && "" !== (key && type && method)) ?? false;

                    if (responseParameters) {
                        keyBase = "response." + type + "." + key;
                        keyFull = keyBase + "." + method;

                        if (403 === +status) {
                            const customMessage = this.translate.instant(keyBase + ".required", { data: message });
                            message = this.translate.instant(keyFull, { data: customMessage });
                        } else if (key === "twig" && method === "render") {
                            message = this.translate.instant(keyFull, { data: message }) + " :  " + error.data;
                        } else if (key === "server" && method === "connect") {
                            message = this.translate.instant(keyFull);
                        } else {
                            message = this.translate.instant(keyFull, { data: message });
                        }
                    } else {
                        switch (httpVerb) {
                            case "GET":
                            case "POST":
                            case "PUT":
                            case "DELETE":
                                const translation = this.translate.instant(["response.error.http.base", `response.error.http.verb.${httpVerb.toLocaleLowerCase()}`, "response.error.http.ressource"]);
                                message = Object.values(translation).join(" ");
                                break;
                            default:
                                message = this.translate.instant("response.error.internal");
                        }
                    }

                    this.modal.error(message);
                }

                return throwError(httpError).toPromise();
            }
        } else {
            return new Promise((resolve, reject) => {
                reject(true);
            });
        }
    }
}
